import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { DialogContentText } from "@mui/material";
import {
  GridActionsCellItem,
  GridActionsCellItemProps,
} from "@mui/x-data-grid-pro";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { ModalDialogConfirmStatus } from "components";
import { AppDispatch } from "store";
import { deleteTable } from "store/reducers/products/tables/tables.actions";
import { ProcessState } from "types/process";

export type TableSummaryGridAction_DeleteProps = Extract<
  GridActionsCellItemProps,
  { showInMenu: true }
> & {
  readonly tableId: string;
  readonly tableType?: string;
};

export function TableSummaryGridAction_Delete({
  tableId,
  tableType,
  closeMenuOnClick = false,
  closeMenu,
  ...props
}: TableSummaryGridAction_DeleteProps) {
  const dispatch = useDispatch<AppDispatch>();

  // State
  const [open, setOpen] = useState(false);
  const [processState, setProcessState] = useState<ProcessState>(
    ProcessState.IDLE
  );
  const [statusMessage, setStatusMessage] = useState("");

  const isBusy =
    processState === ProcessState.INITIALIZING ||
    processState === ProcessState.QUEUED ||
    processState === ProcessState.PROCESSING;

  const resetDialogState = useCallback(() => {
    setProcessState(ProcessState.IDLE);
    setStatusMessage("");
  }, []);

  const handleOpen = useCallback(() => {
    resetDialogState();
    setOpen(true);
  }, [resetDialogState]);

  const handleClose = useCallback(() => {
    if (isBusy) return;

    setOpen(false);
    resetDialogState();
    closeMenu?.();
  }, [closeMenu, isBusy, resetDialogState]);

  const handleDelete = useCallback(async () => {
    try {
      setProcessState(ProcessState.PROCESSING);
      setStatusMessage("Deleting table...");

      await dispatch(deleteTable(tableId));

      const msg = "Table successfully deleted.";
      toast.success(msg);
      setProcessState(ProcessState.COMPLETE);
      setStatusMessage(msg);
      setOpen(false);
      resetDialogState();
    } catch (error) {
      const msg = `Error deleting table: ${error}`;
      console.error(msg);
      toast.error(msg);
      setProcessState(ProcessState.ERROR);
      setStatusMessage(msg);
    }
  }, [dispatch, resetDialogState, tableId]);

  const contentSection = useMemo<ReactNode>(() => {
    const tableLabel = tableType
      ? `${tableType} table ${tableId}`
      : `table ${tableId}`;

    return (
      <DialogContentText>
        Are you sure you want to delete {tableLabel}?
      </DialogContentText>
    );
  }, [tableId, tableType]);

  return (
    <React.Fragment>
      <GridActionsCellItem
        {...props}
        closeMenuOnClick={closeMenuOnClick}
        closeMenu={closeMenu}
        onClick={handleOpen}
      />
      <ModalDialogConfirmStatus
        open={open}
        title="Delete Table"
        maxWidth="sm"
        onClose={handleClose}
        disableEscapeKeyDown
        disableCloseOnBackdropClick={isBusy}
        contentSection={contentSection}
        hideContentOnProcessComplete
        defaultButton="CANCEL"
        cancelButtonText="Cancel"
        okButtonText="OK"
        okButtonWarningColor
        onOKButtonClick={handleDelete}
        onCancelButtonClick={handleClose}
        processState={processState}
        statusMessage={statusMessage}
      />
    </React.Fragment>
  );
}
